import { useSession } from "@clerk/clerk-react";
import axios from "axios";
import {
  getUnexpiredToken,
  useActiveMainCanvas,
  useAutoUpdateRef,
} from "@/utils/utils";
import { usePromise } from "./usePromise";

const terminalServerUrls: Record<string, Promise<string>> = {};

const RETRY_TIMEOUT_MS = 90_000;
const RETRY_INTERVAL_MS = 1_000;

type Options = {
  timeoutMs?: number;
  intervalMs?: number;
};

/**
 * Fetch the terminal token for authentication with the terminal server.
 */
async function fetchTerminalToken(
  canvasId: string,
  getToken: () => Promise<string | null>,
): Promise<string> {
  const token = await getToken();
  if (!token) {
    throw new Error("No auth token available");
  }

  const res = await axios.get(
    `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${canvasId}/terminal-token`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return res.data.token;
}

async function fetchTerminalPortWithRetry(
  canvasId: string,
  getToken: () => Promise<string | null>,
  {
    timeoutMs = RETRY_TIMEOUT_MS,
    intervalMs = RETRY_INTERVAL_MS,
  }: Options = {},
): Promise<string> {
  const startTime = Date.now();

  while (Date.now() - startTime < timeoutMs) {
    const token = await getToken();
    if (!token) {
      return "";
    }

    const res = await axios.get(
      `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${canvasId}/terminal-server-port`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (res.data.port != null) {
      return `http://localhost:${res.data.port}`;
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new Error(
    `Terminal server port not available after ${timeoutMs / 1000}s`,
  );
}

async function buildTerminalServerUrl(
  canvasId: string,
  assignedPort: number | null,
  getToken: () => Promise<string | null>,
  options?: Options,
): Promise<string> {
  // Fetch the terminal token for authentication
  const terminalToken = await fetchTerminalToken(canvasId, getToken);

  let baseUrl: string;
  if (import.meta.env.DEV) {
    baseUrl = await fetchTerminalPortWithRetry(canvasId, getToken, options);
    if (!baseUrl) {
      return "";
    }
  } else {
    baseUrl = `https://${canvasId}.${import.meta.env.VITE_CANVAS_DOMAIN}:9560`;
  }

  // Append the terminal token as a query parameter for authentication
  const url = new URL(baseUrl);
  url.searchParams.set("authHeader", terminalToken);
  return url.toString();
}

export const getTerminalServerUrl = (
  canvasId: string,
  assignedPort: number | null,
  getToken: () => Promise<string | null>,
  options?: Options,
) => {
  const key = import.meta.env.DEV ? `${canvasId}-${assignedPort}` : canvasId;
  const promise =
    terminalServerUrls[key] ??
    buildTerminalServerUrl(canvasId, assignedPort, getToken, options);
  terminalServerUrls[key] = promise;
  return promise;
};

const unresolvedPromise = new Promise<never>(() => {});

export const useTerminalServerUrl = () => {
  const canvas = useActiveMainCanvas();
  const sessionRef = useAutoUpdateRef(useSession());

  return usePromise(
    canvas
      ? getTerminalServerUrl(canvas.id, canvas.assigned_port, () =>
          getUnexpiredToken(sessionRef.current.session),
        )
      : unresolvedPromise,
  );
};
